import { MONITOR_TILES, type MonitorTile } from '@serviceportal/shared';
import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

/*
 * Darstellung der Statusmonitor-Kacheln (öffentliche Anzeige und Vorschau im Editor).
 * Schriftgrößen skalieren mit der Kachel (Container-Query-Einheiten), damit die Anzeige
 * auf Tablet, Fernseher und Ultrawide-Monitor lesbar bleibt.
 */

type Tone = 'default' | 'success' | 'warning' | 'danger';
export interface ListRow {
  id: string;
  title: string;
  subtitle: string | null;
  badge: { label: string; color: string | null } | null;
  since: string | null;
  alert: boolean;
}
export type TileData =
  | { kind: 'stat'; value: number; tone: Tone; unit?: string }
  | { kind: 'statusgrid'; items: { label: string; value: number; color: string | null }[] }
  | { kind: 'gauge'; value: number; target: number; unit: string }
  | { kind: 'list'; rows: ListRow[]; total: number }
  | { kind: 'bars' | 'donut'; items: { label: string; value: number; color: string | null }[] }
  | { kind: 'series'; days: string[]; series: { label: string; values: number[]; tone: Tone }[] }
  | { kind: 'clock'; timezone: string }
  | { kind: 'note'; text: string };

const TONE_TEXT: Record<Tone, string> = { default: 'text-foreground', success: 'text-success', warning: 'text-warning', danger: 'text-destructive' };
const TONE_FILL: Record<Tone, string> = { default: 'fill-primary', success: 'fill-success', warning: 'fill-warning', danger: 'fill-destructive' };
const TONE_BG: Record<Tone, string> = { default: 'bg-primary', success: 'bg-success', warning: 'bg-warning', danger: 'bg-destructive' };
const PALETTE = ['#3b82f6', '#22c55e', '#f59e0b', '#8b5cf6', '#06b6d4', '#ef4444', '#14b8a6', '#f97316', '#64748b'];

/** „seit 3 Std.“ / „seit 2 Tg.“ */
function age(iso: string | null) {
  if (!iso) return '';
  const min = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (min < 60) return `${min} Min.`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h} Std.`;
  return `${Math.round(h / 24)} Tg.`;
}

function Clock({ timezone }: { timezone: string }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="flex h-full flex-col items-center justify-center text-center">
      <p className="font-semibold tabular-nums leading-none text-[min(38cqh,22cqw)]">{now.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', timeZone: timezone })}</p>
      <p className="mt-[4cqh] text-muted-foreground text-[min(10cqh,6.5cqw)]">{now.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', timeZone: timezone })}</p>
    </div>
  );
}

function Stat({ value, tone, unit }: { value: number; tone: Tone; unit?: string }) {
  return (
    <div className="flex h-full items-center justify-center gap-[2cqw]">
      <p className={cn('font-semibold tabular-nums leading-none', unit ? 'text-[min(45cqh,30cqw)]' : 'text-[min(55cqh,40cqw)]', TONE_TEXT[tone])}>{unit ? value.toLocaleString('de-DE') : value}</p>
      {unit && <span className="self-end pb-[8cqh] text-[min(12cqh,8cqw)] text-muted-foreground">{unit}</span>}
    </div>
  );
}

/** Mehrere Status mit Anzahl als farbige Felder */
function StatusGrid({ items }: { items: { label: string; value: number; color: string | null }[] }) {
  if (items.length === 0) return <p className="flex h-full items-center justify-center text-muted-foreground">Keine Daten</p>;
  const cols = Math.min(items.length, items.length > 6 ? 4 : 3);
  return (
    <div className="grid h-full gap-[1.5cqh]" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gridAutoRows: '1fr' }}>
      {items.map((i, n) => (
        <div key={i.label} className="flex min-h-0 flex-col justify-center overflow-hidden rounded-lg border-l-4 bg-muted/40 px-[3%] py-[2%]" style={{ borderLeftColor: i.color ?? PALETTE[n % PALETTE.length] }}>
          <span className={cn('font-semibold tabular-nums leading-none text-[min(14cqh,7cqw)]', i.value === 0 && 'text-muted-foreground')}>{i.value}</span>
          <span className="truncate text-[min(6cqh,3.2cqw)] text-muted-foreground" title={i.label}>
            {i.label}
          </span>
        </div>
      ))}
    </div>
  );
}

/** Halbkreis-Tacho: Fortschritt gegen ein Ziel */
function Gauge({ value, target, unit }: { value: number; target: number; unit: string }) {
  const pct = Math.min(1, target > 0 ? value / target : 0);
  const reached = value >= target;
  const r = 40;
  const len = Math.PI * r;
  return (
    <div className="flex h-full flex-col items-center justify-center">
      <svg viewBox="0 0 100 58" className="h-[70cqh] max-w-full" role="img" aria-label={`${value} von ${target} ${unit}`}>
        <path d="M10 50 A40 40 0 0 1 90 50" fill="none" className="stroke-muted" strokeWidth="9" strokeLinecap="round" />
        <path d="M10 50 A40 40 0 0 1 90 50" fill="none" className={reached ? 'stroke-success' : 'stroke-primary'} strokeWidth="9" strokeLinecap="round" strokeDasharray={`${pct * len} ${len}`} />
        <text x="50" y="45" textAnchor="middle" className="fill-foreground" style={{ fontSize: 18, fontWeight: 600 }}>
          {value}
        </text>
        <text x="50" y="56" textAnchor="middle" className="fill-muted-foreground" style={{ fontSize: 7 }}>
          von {target} {unit}
        </text>
      </svg>
      {reached && <span className="text-[min(7cqh,4cqw)] font-medium text-success">Ziel erreicht</span>}
    </div>
  );
}

/** Säulendiagramm für Verteilungen */
function Columns({ items }: { items: { label: string; value: number; color: string | null }[] }) {
  if (items.length === 0) return <p className="flex h-full items-center justify-center text-muted-foreground">Keine Daten</p>;
  const shown = items.slice(0, 10);
  const max = Math.max(1, ...shown.map((i) => i.value));
  return (
    <div className="flex h-full items-end gap-[2%]">
      {shown.map((i, n) => (
        <div key={i.label} className="flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-1">
          <span className="text-[min(7cqh,4cqw)] font-medium tabular-nums">{i.value}</span>
          <div className="w-full rounded-t-md" style={{ height: `${Math.max(2, (i.value / max) * 65)}%`, backgroundColor: i.color ?? PALETTE[n % PALETTE.length] }} title={`${i.label}: ${i.value}`} />
          <span className="w-full truncate text-center text-[min(5.5cqh,3cqw)] text-muted-foreground" title={i.label}>
            {i.label}
          </span>
        </div>
      ))}
    </div>
  );
}

function List({ rows, total }: { rows: ListRow[]; total: number }) {
  if (rows.length === 0) return <p className="flex h-full items-center justify-center text-muted-foreground text-[min(8cqh,5cqw)]">Keine Einträge</p>;
  return (
    <div className="flex h-full flex-col overflow-hidden">
      <ul className="min-h-0 flex-1 divide-y divide-border/60 overflow-hidden">
        {rows.map((r) => (
          <li key={r.id} className={cn('flex items-center gap-3 py-1.5', r.alert && 'text-destructive')}>
            {r.alert && <span className="size-2 shrink-0 rounded-full bg-destructive" aria-label="Achtung" />}
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium">{r.title}</p>
              {r.subtitle && <p className="truncate text-xs text-muted-foreground">{r.subtitle}</p>}
            </div>
            {r.badge && (
              <span className="shrink-0 rounded-full px-2 py-0.5 text-xs font-medium text-white" style={{ backgroundColor: r.badge.color ?? '#64748b' }}>
                {r.badge.label}
              </span>
            )}
            <span className="w-14 shrink-0 text-right text-xs tabular-nums text-muted-foreground">{age(r.since)}</span>
          </li>
        ))}
      </ul>
      {total > rows.length && <p className="pt-1 text-right text-xs text-muted-foreground">+ {total - rows.length} weitere</p>}
    </div>
  );
}

function Bars({ items }: { items: { label: string; value: number; color: string | null }[] }) {
  if (items.length === 0) return <p className="flex h-full items-center justify-center text-muted-foreground">Keine Daten</p>;
  const max = Math.max(1, ...items.map((i) => i.value));
  return (
    <ul className="flex h-full flex-col justify-center gap-[3cqh] overflow-hidden">
      {items.slice(0, 8).map((i, n) => (
        <li key={i.label} className="grid grid-cols-[minmax(0,40%)_1fr_auto] items-center gap-3 text-[min(7cqh,4.5cqw)]">
          <span className="truncate" title={i.label}>
            {i.label}
          </span>
          <span className="h-[1em] rounded-full bg-muted">
            <span className="block h-full rounded-full" style={{ width: `${(i.value / max) * 100}%`, backgroundColor: i.color ?? PALETTE[n % PALETTE.length] }} />
          </span>
          <span className="tabular-nums font-medium">{i.value}</span>
        </li>
      ))}
    </ul>
  );
}

function Donut({ items }: { items: { label: string; value: number; color: string | null }[] }) {
  const total = items.reduce((s, i) => s + i.value, 0);
  if (total === 0) return <p className="flex h-full items-center justify-center text-muted-foreground">Keine Daten</p>;
  const r = 15.9155; // Umfang 100
  let offset = 25;
  return (
    <div className="flex h-full items-center gap-[4cqw]">
      <div className="relative aspect-square h-[80cqh] max-w-[50cqw] shrink-0">
        <svg viewBox="0 0 42 42" className="size-full -rotate-0" role="img" aria-label={items.map((i) => `${i.label}: ${i.value}`).join(', ')}>
          <circle cx="21" cy="21" r={r} fill="none" className="stroke-muted" strokeWidth="6" />
          {items.map((i, n) => {
            const pct = (i.value / total) * 100;
            const el = <circle key={i.label} cx="21" cy="21" r={r} fill="none" stroke={i.color ?? PALETTE[n % PALETTE.length]} strokeWidth="6" strokeDasharray={`${pct} ${100 - pct}`} strokeDashoffset={offset} />;
            offset -= pct;
            return el;
          })}
        </svg>
        <span className="absolute inset-0 flex items-center justify-center font-semibold tabular-nums text-[min(18cqh,10cqw)]">{total}</span>
      </div>
      <ul className="min-w-0 flex-1 space-y-[2cqh] text-[min(6.5cqh,4cqw)]">
        {items.map((i, n) => (
          <li key={i.label} className="flex items-center gap-2">
            <span className="size-[0.8em] shrink-0 rounded-sm" style={{ backgroundColor: i.color ?? PALETTE[n % PALETTE.length] }} />
            <span className="truncate">{i.label}</span>
            <span className="ml-auto tabular-nums font-medium">{i.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const TONE_STROKE: Record<Tone, string> = { default: 'stroke-primary', success: 'stroke-success', warning: 'stroke-warning', danger: 'stroke-destructive' };

function Series({ days, series, style = 'columns' }: { days: string[]; series: { label: string; values: number[]; tone: Tone }[]; style?: 'columns' | 'line' }) {
  const max = Math.max(1, ...series.flatMap((s) => s.values));
  const w = 100 / days.length;
  const bw = (w * 0.8) / series.length;
  const fmt = (d: string) => new Date(`${d}T12:00:00`).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  return (
    <div className="flex h-full flex-col">
      <svg viewBox="0 0 100 50" preserveAspectRatio="none" className="min-h-0 w-full flex-1" role="img" aria-label={series.map((s) => `${s.label}: ${s.values.reduce((a, b) => a + b, 0)}`).join(', ')}>
        {style === 'line' &&
          series.map((s) => {
            const step = days.length > 1 ? 100 / (days.length - 1) : 0;
            const pts = s.values.map((v, i) => `${(i * step).toFixed(2)},${(49 - (v / max) * 46).toFixed(2)}`).join(' ');
            return <polyline key={s.label} points={pts} fill="none" className={TONE_STROKE[s.tone]} strokeWidth="1.2" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />;
          })}
        {style === 'columns' && days.map((d, i) =>
          series.map((s, k) => {
            const v = s.values[i] ?? 0;
            const h = (v / max) * 48;
            return (
              <rect key={`${d}-${k}`} x={i * w + w * 0.1 + k * bw} y={50 - h} width={bw * 0.9} height={h} className={TONE_FILL[s.tone]}>
                <title>{`${fmt(d)}: ${v} ${s.label}`}</title>
              </rect>
            );
          }),
        )}
      </svg>
      <div className="mt-1 flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>{fmt(days[0]!)}</span>
        <span className="flex gap-3">
          {series.map((s) => (
            <span key={s.label} className="inline-flex items-center gap-1">
              <span className={cn('size-2 rounded-sm', TONE_BG[s.tone])} /> {s.label}: <span className="font-medium text-foreground">{s.values.at(-1)}</span> heute
            </span>
          ))}
        </span>
        <span>{fmt(days.at(-1)!)}</span>
      </div>
    </div>
  );
}

function Body({ data, tile }: { data: TileData | undefined; tile: MonitorTile }) {
  if (!data) return <p className="text-muted-foreground">Keine Daten</p>;
  const chart = tile.options.chart;
  switch (data.kind) {
    case 'stat':
      return <Stat value={data.value} tone={data.tone} unit={data.unit} />;
    case 'statusgrid':
      return <StatusGrid items={data.items} />;
    case 'gauge':
      return <Gauge value={data.value} target={data.target} unit={data.unit} />;
    case 'list':
      return <List rows={data.rows} total={data.total} />;
    case 'bars':
    case 'donut': {
      // Darstellung je Kachel wählbar (Balken, Säulen, Ring)
      const variant = chart ?? (data.kind === 'donut' ? 'donut' : 'bars');
      return variant === 'donut' ? <Donut items={data.items} /> : variant === 'columns' ? <Columns items={data.items} /> : <Bars items={data.items} />;
    }
    case 'series':
      return <Series days={data.days} series={data.series} style={tile.options.seriesStyle} />;
    case 'clock':
      return <Clock timezone={data.timezone} />;
    case 'note':
      return <p className="h-full overflow-hidden whitespace-pre-wrap text-[min(9cqh,5.5cqw)] leading-snug">{data.text}</p>;
  }
}

export function MonitorTileView({ tile, data, className }: { tile: MonitorTile; data: TileData | undefined; className?: string }) {
  const def = MONITOR_TILES[tile.key];
  const showTitle = tile.key !== 'clock';
  return (
    <section className={cn('flex min-h-0 flex-col rounded-xl border bg-card p-[clamp(0.5rem,1.2vw,1.5rem)] shadow-sm', className)} style={{ gridColumn: `span ${tile.w}`, gridRow: `span ${tile.h}` }}>
      {showTitle && <h2 className="mb-1 truncate text-[clamp(0.75rem,1vw,1.25rem)] font-medium text-muted-foreground">{tile.title || def.label}</h2>}
      <div className="@container min-h-0 flex-1 text-[clamp(0.8rem,1vw,1.3rem)]">
        <Body data={data} tile={tile} />
      </div>
    </section>
  );
}

/** Raster der Kacheln; füllt die verfügbare Höhe, auf schmalen Bildschirmen scrollbar. */
export function MonitorGrid({ columns, tiles, data, className }: { columns: number; tiles: MonitorTile[]; data: Record<string, TileData | undefined>; className?: string }) {
  return (
    <div
      className={cn('grid grid-flow-row-dense gap-[clamp(0.5rem,1vw,1.25rem)] max-md:!grid-cols-1 max-md:auto-rows-[minmax(11rem,auto)] md:auto-rows-fr', className)}
      style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
    >
      {tiles.map((t) => (
        <MonitorTileView key={t.id} tile={{ ...t, w: Math.min(t.w, columns) }} data={data[t.id]} className="max-md:!col-span-1 max-md:![grid-row:span_1]" />
      ))}
    </div>
  );
}
